import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { RefObject, useCallback, useMemo, useRef } from "react";
import { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import { Commodity } from "@/pages/sy/models/Commodity";
import { RecipeLayer } from "@/pages/sy/models/RecipeLayer";
import { chargeOperationStyles } from "@/pages/sy/styles/ChargeOperationStyles";
import {
  findMatchingRecipeLayer,
  type RequestedWeightLayerSuggestions,
} from "../utils/heatWeightScaling";
import { LayerAlignmentRow } from "../utils/layerAlignment";
import { LayerRow } from "./LayerRow";

const getLayerKey = (layer: ChargeLayer) =>
  layer.ChargeLayerId ?? layer.LayerNumber;

const calculateBaseDiff = (
  currentLayer: ChargeLayer,
  recipeLayers: RecipeLayer[],
) => {
  // Prefer server-provided mapping/target for stability after reorder/delete
  if (typeof currentLayer.RecipeTargetWeight === "number") {
    return (currentLayer.RecipeTargetWeight || 0) - (currentLayer.Weight || 0);
  }

  if (currentLayer.RecipeLayerId) {
    const recipeLayer = recipeLayers.find(
      (layer) => layer.RecipeLayerId === currentLayer.RecipeLayerId,
    );
    if (recipeLayer) {
      return (recipeLayer.Weight || 0) - (currentLayer.Weight || 0);
    }
  }

  if (!currentLayer.CommodityId) return 0;

  const recipeLayer = recipeLayers.find(
    (layer) =>
      layer.CommodityId === currentLayer.CommodityId &&
      layer.LayerNumber === currentLayer.LayerNumber,
  );

  return recipeLayer
    ? (recipeLayer.Weight || 0) - (currentLayer.Weight || 0)
    : 0;
};

export const TableCurrentLayers = ({
  recipeLayers,
  alignedRows,
  requestedWeightSuggestions,
  commodities,
  selectedLayer,
  getCommodityColor,
  getBgColorWithAlpha,
  handleLayerSelect,
  onOpenCommodityModal,
  longPressDuration,
  containerRef,
  onScroll,
}: {
  recipeLayers: RecipeLayer[];
  alignedRows: LayerAlignmentRow[];
  requestedWeightSuggestions?: RequestedWeightLayerSuggestions | null;
  commodities: Commodity[];
  selectedLayer: ChargeLayer | null;
  getCommodityColor: (commodityId: number, commodities: Commodity[]) => string;
  getBgColorWithAlpha: (color: string, alpha: number) => string;
  handleLayerSelect: (layer: ChargeLayer) => void;
  onOpenCommodityModal: (layer: ChargeLayer) => void;
  longPressDuration: number;
  containerRef?: RefObject<HTMLDivElement>;
  onScroll?: () => void;
}) => {
  const theme = useTheme();
  const styles = useMemo(() => chargeOperationStyles(theme), [theme]);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);

  const startLongPress = useCallback(
    (layer: ChargeLayer) => {
      longPressTriggeredRef.current = false;
      longPressTimerRef.current = window.setTimeout(() => {
        longPressTriggeredRef.current = true;
        handleLayerSelect(layer);
        onOpenCommodityModal(layer);
      }, longPressDuration);
    },
    [onOpenCommodityModal, handleLayerSelect, longPressDuration],
  );

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleClick = useCallback(
    (layer: ChargeLayer) => {
      // Only select if long press wasn't triggered
      if (!longPressTriggeredRef.current) {
        handleLayerSelect(layer);
      }
      longPressTriggeredRef.current = false;
    },
    [handleLayerSelect],
  );

  const layerDiffsByKey = useMemo(() => {
    return new Map(
      alignedRows.flatMap(({ chargeLayer }) => {
        if (!chargeLayer) return [];

        if (requestedWeightSuggestions) {
          const recipeLayerId = findMatchingRecipeLayer(
            chargeLayer,
            recipeLayers,
          )?.RecipeLayerId;

          return [
            [
              getLayerKey(chargeLayer),
              recipeLayerId == null
                ? null
                : (requestedWeightSuggestions.remainingWeightsByRecipeLayerId.get(
                    recipeLayerId,
                  ) ?? null),
            ] as const,
          ];
        }

        return [
          [
            getLayerKey(chargeLayer),
            calculateBaseDiff(chargeLayer, recipeLayers),
          ] as const,
        ];
      }),
    );
  }, [alignedRows, recipeLayers, requestedWeightSuggestions]);

  return (
    <TableContainer
      ref={containerRef}
      onScroll={onScroll}
      sx={styles.tableContainer}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={styles.tableHeader}>
            <TableCell width="10%" align="center">
              #
            </TableCell>
            <TableCell width="40%">Commodity</TableCell>
            <TableCell width="15%" align="center">
              Weight
            </TableCell>
            <TableCell width="15%" align="center">
              Diff
            </TableCell>
            <TableCell width="10%" align="center">
              Manual
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alignedRows.length === 0 ? (
            <TableRow sx={styles.currentLayersTableRow}>
              <TableCell colSpan={5} align="center">
                No layers to display
              </TableCell>
            </TableRow>
          ) : (
            alignedRows.map(({ chargeLayer: layer, recipeLayer }, index) => {
              if (!layer) {
                return (
                  <TableRow
                    key={`current-layer-gap-${recipeLayer?.RecipeLayerId ?? index}`}
                    aria-hidden="true"
                    sx={{
                      ...(styles.currentLayersTableRow as object),
                      cursor: "default",
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "white" },
                    }}
                  >
                    <TableCell colSpan={5}>{"\u00A0"}</TableCell>
                  </TableRow>
                );
              }

              const commodityColor = getCommodityColor(
                layer.CommodityId as number,
                commodities,
              );
              const bgColor = getBgColorWithAlpha(commodityColor, 0.1);
              const diff = layerDiffsByKey.get(getLayerKey(layer));

              return (
                <LayerRow
                  key={`layer-${layer.ChargeLayerId}`}
                  layer={layer}
                  commodityColor={commodityColor}
                  bgColor={bgColor}
                  diff={diff === undefined ? 0 : diff}
                  isSelected={
                    selectedLayer?.ChargeLayerId === layer.ChargeLayerId
                  }
                  styles={styles}
                  getBgColorWithAlpha={getBgColorWithAlpha}
                  onStartLongPress={() => startLongPress(layer)}
                  onCancelLongPress={cancelLongPress}
                  onClick={() => handleClick(layer)}
                />
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
